import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import DayJS from 'dayjs';

import { FeatureItems } from '@marketing/components/features/FeatureItems';

import {
    CompanyProfile,
    CompanySubscription,
    CompanySubscriptionConfig,
    PermissionCompany,
    SubscriptionType,
} from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { MarketingHelpers } from 'helpers/MarketingHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { useCurrentProfile } from 'components/company-profile/hooks/use-current-profile';
import { useProfilePermission } from 'components/company-profile/hooks/use-profile-permission';
import { ReplusLogo } from 'components/logo/ReplusLogo';
import { useCurrentTeam } from 'hooks/use-current-team';
import { usePrices } from 'hooks/use-prices';
import { CompanyProfileService } from 'services/CompanyProfileService';

import cx from './UpgradeMessageModal.module.css';

const premiumConfig = CompanySubscriptionConfig[CompanySubscription.PREMIUM];

const DAYS_AFTER_CLAIMING = 1;

const UpgradeMessageModal = () => {
    const router = useRouter();

    const team = useCurrentTeam();
    const company = useCurrentProfile();
    const canEdit = useProfilePermission(PermissionCompany.EDIT);

    const subscription = SubscriptionHelpers.getSubscriptionData(
        SubscriptionType.COMPANY,
        team?.subscriptions,
    ).subscription;

    const [dismissed, setDismissed] = useState(false);
    const [redirecting, setRedirecting] = useState(false);

    const upgradeAction = router.query.action === 'upgrade';

    const opened =
        redirecting ||
        Boolean(
            !dismissed &&
                !upgradeAction &&
                canEdit &&
                team &&
                company &&
                showUpgradeMessage(company) &&
                subscription !== CompanySubscription.PREMIUM,
        );

    const markUpgradeMessageAsSeen = () => {
        return company ? CompanyProfileService.dismissUpgradeMessage(company.id) : Promise.resolve();
    };

    const dismissUpgradeMessage = () => {
        setDismissed(true);

        markUpgradeMessageAsSeen();
    };

    useEffect(() => {
        if (upgradeAction && canEdit && company?.showUpgradeMessage) {
            dismissUpgradeMessage();
        }
    }, [upgradeAction, canEdit, company?.id, company?.showUpgradeMessage]);

    const discoverPricing = async () => {
        setRedirecting(true);

        await markUpgradeMessageAsSeen();

        router.push(
            MarketingHelpers.urls.resourcepro.pricing(company ? CompanyHelpers.urls.upgrade(company.slug) : undefined),
        );
    };

    return (
        <Modal
            opened={opened}
            size="lg"
            styles={{ body: { padding: 0 } }}
            onClose={dismissUpgradeMessage}
            closeOnEscape={false}
            closeOnClickOutside={false}
            withCloseButton={false}
        >
            <UpgradeMessage
                redirecting={redirecting}
                onDiscoverPricing={discoverPricing}
                onDismiss={dismissUpgradeMessage}
            />
        </Modal>
    );
};

const showUpgradeMessage = (company: CompanyProfile) => {
    if (!company.showUpgradeMessage) return false;

    if (!company.claimedAt) return true;

    const firstShownAt = DayJS(company.claimedAt).add(DAYS_AFTER_CLAIMING, 'day');

    return !DayJS().isBefore(firstShownAt);
};

const UpgradeMessage = ({
    redirecting,
    onDiscoverPricing,
    onDismiss,
}: {
    redirecting: boolean;
    onDiscoverPricing: () => void;
    onDismiss: () => void;
}) => {
    const { prices } = usePrices();

    const yearlyPrice = prices[CompanySubscription.PREMIUM]?.yearly;

    return (
        <Stack gap={0}>
            <Stack className={cx.banner} p="xl" gap="xs" align="center" ta="center">
                <ReplusLogo c="white" w={90} />

                <Title order={2} c="white" size={24} fw={800}>
                    Upgrade to Premium
                </Title>

                <Text c="white" opacity={0.8}>
                    {premiumConfig.subtitle}
                    {yearlyPrice ? ` · ${IntlHelpers.formatPrice(yearlyPrice, 0)} per year` : ''}
                </Text>
            </Stack>

            <Stack p="xl" gap="lg">
                <Text fz={14}>
                    <Text span inherit fw={600}>
                        Congratulations on claiming your profile!
                    </Text>{' '}
                    It is live and included in search results.{' '}
                    <Text span inherit fw={600}>
                        Upgrade to Premium
                    </Text>{' '}
                    to get in front of buyers while they are searching, and to turn QR code scans at RE+ events into
                    engaged leads:
                </Text>

                <FeatureItems
                    highlight
                    spacing="md"
                    items={premiumConfig.features.map((feature) => ({
                        title: feature.title,
                        description: feature.description,
                    }))}
                />

                <Group justify="center" mt="sm">
                    <Button size="md" onClick={onDiscoverPricing} loading={redirecting}>
                        Discover Pricing
                    </Button>
                </Group>

                <Group justify="center">
                    <Button size="md" variant="transparent" onClick={onDismiss} disabled={redirecting}>
                        Continue to profile and subscribe later
                    </Button>
                </Group>
            </Stack>
        </Stack>
    );
};

export { UpgradeMessageModal };
